
import Vue from 'vue';
import PianoCustomerStateHandler from '@piano/components/PianoCustomerStateHandler.vue';
import Podcast from '@root/modules/podcast/components/fragments/Podcast.vue';
import { Podcast as IPodcast } from '@root/modules/podcast/types/podcast';
import getPodcastWithHref from '@root/modules/podcast/utils/getPodcastWithHref';
import Swiper from '@core/components/UI/swiper/Swiper.vue';
import LinkHandler from '@core/components/helpers/LinkHandler.vue';
import isMobileView from '@root/common/utils/isMobileView';
import { getListenedDuration, getListenedIds } from '@root/modules/podcast/utils/customerData';
import PodcastEpisodeService from '@root/modules/podcast/services/podcastEpisode.service';
import getMediaData from '@root/modules/podcast/utils/getMediaData';
import resort from '@root/modules/podcast/utils/resort';

interface Data {
  data: IPodcast[];
  isMobileView: boolean;
  swiperOptions: Record<string, any>;
}

interface Computed {
  customerToken: string | null;
  items: IPodcast[];
}

interface Methods {
  getListeningHistory: () => void;
}

export default Vue.extend<Data, Methods, Computed, unknown>({
  components: {
    PianoCustomerStateHandler,
    Podcast,
    Swiper,
    LinkHandler,
  },
  data() {
    return {
      data: [],
      isMobileView: false,
      swiperOptions: {
        spaceBetween: 24,
        initialSlide: 0,
        autoplay: false,
        loop: false,
        slidesPerView: 2.5,
        slidesPerGroup: 1,
        pagination: {
          enabled: false,
        },
        navigation: {
          enabled: false,
        },
        scrollbar: {
          enabled: false,
        },
        simulateTouch: false,
        breakpoints: {
          768: {
            freeMode: false,
            slidesPerView: 4,
            slidesPerGroup: 4,
          },
          1230: {
            freeMode: false,
            slidesPerView: 6,
            slidesPerGroup: 6,
          },
        },
      },
    };
  },
  computed: {
    items() {
      let data = [...this.data];

      if (this.data.length) {
        const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
        const podcastsWithHref = data.map((podcast: IPodcast) => {
          const { domain } = this.$channelConfig('settings');
          return getPodcastWithHref({ podcast, domain, fakeDomainEnabled });
        });

        data = podcastsWithHref;
      }

      return data;
    },
    customerToken() {
      return this.$store.state.piano.token;
    },
  },
  watch: {
    customerToken(value) {
      if (value) {
        this.getListeningHistory();
      }
    },
  },
  methods: {
    async getListeningHistory() {
      if (!this.customerToken) {
        return;
      }

      const listenedIds = await getListenedIds(undefined, this.customerToken);
      if (listenedIds?.items?.length) {
        const articleIds = listenedIds.items?.map((listenedId) => listenedId.value);
        const listenedDurations = await getListenedDuration(articleIds, this.customerToken);

        const podcastEpisodeService = new PodcastEpisodeService();
        const articles = await podcastEpisodeService.fetch({ id: articleIds.map((id) => Number(id)) });
        if (articles?.length) {
          const mediaData = await getMediaData(articles, null, false);
          const data = [];
          for (const article of resort(articles, articleIds)) {
            const duration = mediaData?.find((mediaDataItem) => mediaDataItem.id === article.id)?.assetDuration || null;
            const video = mediaData?.find((mediaDataItem) => mediaDataItem.id === article.id)?.video || undefined;
            const listenedDuration = Number(listenedDurations?.find((item) => item.key === `podcast-listened-duration--${article.id}`)?.value);

            data.push({ ...article, duration, listenedDuration, video });
          }

          this.data = data;
        }
      }
    },
  },
  async mounted() {
    this.isMobileView = isMobileView();

    if (this.customerToken) {
      this.getListeningHistory();
    }
  },
});
